import React from 'react'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { IconList } from '../../../../../shared/icon/icon'
import { Menu, MenuPurge, MenuRestore } from '../../../../../shared/menu/menu'
import { CardFullHeightScrollY } from '../../../../../shared/card/card-full-height-scroll-y'
import { useApiListUserLogin } from '../../../../../api-new/state/content'
import { useRoleOrgUser } from '../../../../../role/hook/role-org'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ColumnText, ColumnMenu, ColumnFormat } from '../../../../../shared/table-builder/table-columns'
import { ROW_HEIGHT_TWO_LINE } from '../../../../../shared/table-builder/table-ag'
import { formatDateTime } from '../../../../../shared/sso-workspace/format'
import { removalLine, removalOf } from './removal-words'
import { useNotificationContext } from '../../../../../context/notification/context'
import { recycleErrorMessage } from '../../../../../shared/error/server-message'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'

// The toast for a restore. The server gives back only the roles the
// restorer could grant themselves and names the rest in droppedRoles.
export const restoreNotice = result => {
  const dropped = (result && result.droppedRoles) || []

  if (dropped.length === 0) {
    return { text: 'Restored', duration: 3000 }
  }

  return {
    text: `Restored. These roles weren't restored because you can't grant them: ${dropped.map(role => role.name).join(', ')}`,
    duration: 10000
  }
}

// Who removed the member and when, and whether SSO sign-in would add them
// back (spec R25): "Removed by Kim Lee on Oct 23, 2026." over "SSO sign-in
// won't add them back until you restore them." Restore stays one click
// (the row's pinned Restore).
export const removedCell = cell => {
  const removal = removalOf(cell.data)
  if (!removal) return ''
  return (
    <span className="min-w-0" title={cell.data.removedAt ? formatDateTime(cell.data.removedAt) : undefined}>
      <span>{removal.text}</span>
      {removal.sticky && <span className="sw-cell-sub text-body-secondary">{removal.sticky}</span>}
    </span>
  )
}

export function UserRecycle() {
  const apiListUserLogin = useApiListUserLogin()

  const roleOrgUser = useRoleOrgUser()

  const notificationContext = useNotificationContext()

  const [error, failure, setError] = useErrorLine()

  const handleReady = () => apiListUserLogin.get('deleted')
  const handleDestroyed = () => apiListUserLogin.init()

  const handleRestore = async data => {
    setError(null)
    try {
      const result = await apiListUserLogin.put(`${data.userID}/restore`, data)
      const notice = restoreNotice(result)
      notificationContext.toast.notify(notice.text, notice.duration)
    } catch (err) {
      setError(recycleErrorMessage(err, 'That member was already removed.'), err)
    }
    await apiListUserLogin.get('deleted')
  }

  return (
    <React.Fragment>
      <BreadcrumbView Icon={IconList} text="Recycle bin">
        <CardFullHeightScrollY title="recycle bin">
          {error &&
            <p className="text-danger" role="alert">
              {error}
            </p>}
          {error && <ErrorReference error={failure} className="mb-3" />}
          <TableAgClient apiList={apiListUserLogin} onReady={handleReady} onDestoryed={handleDestroyed} rowHeight={ROW_HEIGHT_TWO_LINE}>
            <ColumnText field="email" sortable filter />
            <ColumnText field="firstName" sortable filter />
            <ColumnText field="lastName" sortable filter />
            <ColumnFormat field="removedAt" header="Removed" render={removedCell} exportValue={removalLine} stacked sortable />
            <ColumnMenu
              menu={row =>
                <Menu small>
                  <MenuRestore onClick={() => handleRestore(row)} show={roleOrgUser.accessDelete} />
                  <MenuPurge to={`/org/user/list/recycle/purge/${row.userID}`} show={roleOrgUser.accessDelete} />
                </Menu>}
            />
          </TableAgClient>
        </CardFullHeightScrollY>
      </BreadcrumbView>
    </React.Fragment>
  )
}
